// src/components/admin/flows/panels/ActionConfigForm.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { ACTION_TYPES } from '../canvas/nodeRegistry';
import documentBuilderService from '../../../../services/documentBuilder.service';

const DOCUMENT_TYPES = [
    { value: 'offer_letter', label: 'Offer Letter' },
    { value: 'appointment_letter', label: 'Appointment Letter' },
    { value: 'experience_letter', label: 'Experience Letter' },
    { value: 'relieving_letter', label: 'Relieving Letter' },
    { value: 'salary_slip', label: 'Salary Slip' },
    { value: 'increment_letter', label: 'Increment Letter' },
    { value: 'warning_letter', label: 'Warning Letter' },
    { value: 'termination_letter', label: 'Termination Letter' },
    { value: 'internship_letter', label: 'Internship Letter' },
    { value: 'noc_letter', label: 'NOC Letter' },
    { value: 'custom', label: 'Custom Template' },
];

const REPORT_TYPES = [
    { value: 'attendance_summary', label: 'Attendance Summary' },
    { value: 'leave_balance', label: 'Leave Balance Report' },
    { value: 'payroll_summary', label: 'Payroll Summary' },
    { value: 'headcount', label: 'Headcount Report' },
    { value: 'performance_summary', label: 'Performance Summary' },
];

const ActionConfigForm = ({ config, onChange }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [documentTemplates, setDocumentTemplates] = useState([]);

    // Load document templates when create_document is selected
    useEffect(() => {
        if (config.action_type === 'create_document' && documentTemplates.length === 0) {
            documentBuilderService.getDocumentTemplates()
                .then(templates => {
                    if (Array.isArray(templates) && templates.length > 0) {
                        setDocumentTemplates(templates);
                    }
                })
                .catch(() => {});
        }
    }, [config.action_type]);

    const inputClass = `w-full px-3 py-2 rounded-lg text-sm border ${
        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
    } focus:ring-2 focus:ring-blue-500 focus:outline-none`;

    const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

    const handleTypeChange = (action_type) => {
        const defaults = {
            send_notification: { title: '', message: '' },
            send_email: { to: '{{employee.email}}', subject: '', body: '' },
            create_task: { title: '', description: '', assignee_type: 'trigger_employee', priority: 'medium' },
            assign_task: { task_id: '', assignee_type: 'trigger_employee' },
            update_employee_field: { field: '', value: '' },
            create_document: { template_id: '', employee_type: 'trigger_employee' },
            add_to_checklist: { items: '' },
            run_report: { report_type: '' },
            process_payroll: { period: 'current_month', regularization_reminder: false },
            generate_payslips: { period: 'from_trigger', notify_employees: true },
        };
        onChange({ ...config, action_type, params: defaults[action_type] || {} });
    };

    const updateParam = (key, value) => {
        onChange({ ...config, params: { ...config.params, [key]: value } });
    };

    const renderParamsForm = () => {
        switch (config.action_type) {
            case 'send_notification':
                return (
                    <>
                        <div>
                            <label className={labelClass}>Title</label>
                            <input
                                type="text"
                                value={config.params?.title || ''}
                                onChange={(e) => updateParam('title', e.target.value)}
                                className={inputClass}
                                placeholder="Notification title..."
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Message</label>
                            <textarea
                                value={config.params?.message || ''}
                                onChange={(e) => updateParam('message', e.target.value)}
                                className={`${inputClass} min-h-[80px] resize-y`}
                                placeholder="Use {{employee.name}} for variables..."
                            />
                        </div>
                    </>
                );

            case 'send_email':
                return (
                    <>
                        <div>
                            <label className={labelClass}>To</label>
                            <input
                                type="text"
                                value={config.params?.to || ''}
                                onChange={(e) => updateParam('to', e.target.value)}
                                className={inputClass}
                                placeholder="{{employee.email}} or specific email"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Subject</label>
                            <input
                                type="text"
                                value={config.params?.subject || ''}
                                onChange={(e) => updateParam('subject', e.target.value)}
                                className={inputClass}
                                placeholder="Email subject..."
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Body</label>
                            <textarea
                                value={config.params?.body || ''}
                                onChange={(e) => updateParam('body', e.target.value)}
                                className={`${inputClass} min-h-[100px] resize-y`}
                                placeholder="Email body..."
                            />
                        </div>
                    </>
                );

            case 'create_task':
                return (
                    <>
                        <div>
                            <label className={labelClass}>Task Title</label>
                            <input
                                type="text"
                                value={config.params?.title || ''}
                                onChange={(e) => updateParam('title', e.target.value)}
                                className={inputClass}
                                placeholder="Task title..."
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Description</label>
                            <textarea
                                value={config.params?.description || ''}
                                onChange={(e) => updateParam('description', e.target.value)}
                                className={`${inputClass} min-h-[60px] resize-y`}
                                placeholder="Task description..."
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Assign To</label>
                            <select
                                value={config.params?.assignee_type || 'trigger_employee'}
                                onChange={(e) => updateParam('assignee_type', e.target.value)}
                                className={inputClass}
                            >
                                <option value="trigger_employee">Triggering Employee</option>
                                <option value="reporting_manager">Reporting Manager</option>
                                <option value="hr_admin">HR Admin</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Priority</label>
                            <select
                                value={config.params?.priority || 'medium'}
                                onChange={(e) => updateParam('priority', e.target.value)}
                                className={inputClass}
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                    </>
                );

            case 'update_employee_field':
                return (
                    <>
                        <div>
                            <label className={labelClass}>Field</label>
                            <input
                                type="text"
                                value={config.params?.field || ''}
                                onChange={(e) => updateParam('field', e.target.value)}
                                className={inputClass}
                                placeholder="e.g. employment_status"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Value</label>
                            <input
                                type="text"
                                value={config.params?.value || ''}
                                onChange={(e) => updateParam('value', e.target.value)}
                                className={inputClass}
                                placeholder="New value..."
                            />
                        </div>
                    </>
                );

            case 'add_to_checklist':
                return (
                    <div>
                        <label className={labelClass}>Checklist Items</label>
                        <textarea
                            value={config.params?.items || ''}
                            onChange={(e) => updateParam('items', e.target.value)}
                            className={`${inputClass} min-h-[100px] resize-y`}
                            placeholder="One item per line..."
                        />
                        <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Enter one checklist item per line
                        </p>
                    </div>
                );

            case 'create_document':
                return (
                    <>
                        <div>
                            <label className={labelClass}>Document Type</label>
                            <select
                                value={config.params?.document_type || ''}
                                onChange={(e) => updateParam('document_type', e.target.value)}
                                className={inputClass}
                            >
                                <option value="">Select document type...</option>
                                {DOCUMENT_TYPES.map(dt => (
                                    <option key={dt.value} value={dt.value}>{dt.label}</option>
                                ))}
                            </select>
                        </div>
                        {documentTemplates.length > 0 && (
                            <div>
                                <label className={labelClass}>Template</label>
                                <select
                                    value={config.params?.template_id || ''}
                                    onChange={(e) => updateParam('template_id', e.target.value)}
                                    className={inputClass}
                                >
                                    <option value="">Use default template</option>
                                    {documentTemplates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name || t.title}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className={labelClass}>Generate For</label>
                            <select
                                value={config.params?.employee_type || 'trigger_employee'}
                                onChange={(e) => updateParam('employee_type', e.target.value)}
                                className={inputClass}
                            >
                                <option value="trigger_employee">Triggering Employee</option>
                                <option value="all_employees">All Employees</option>
                            </select>
                        </div>
                    </>
                );

            case 'run_report':
                return (
                    <>
                        <div>
                            <label className={labelClass}>Report Type</label>
                            <select
                                value={config.params?.report_type || ''}
                                onChange={(e) => updateParam('report_type', e.target.value)}
                                className={inputClass}
                            >
                                <option value="">Select report type...</option>
                                {REPORT_TYPES.map(rt => (
                                    <option key={rt.value} value={rt.value}>{rt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Send To</label>
                            <input
                                type="text"
                                value={config.params?.send_to || ''}
                                onChange={(e) => updateParam('send_to', e.target.value)}
                                className={inputClass}
                                placeholder="Email or {{employee.email}}"
                            />
                        </div>
                    </>
                );

            case 'process_payroll':
                return (
                    <>
                        <div>
                            <label className={labelClass}>Payroll Period</label>
                            <select
                                value={config.params?.period || 'current_month'}
                                onChange={(e) => updateParam('period', e.target.value)}
                                className={inputClass}
                            >
                                <option value="current_month">Current month</option>
                                <option value="previous_month">Previous month</option>
                            </select>
                        </div>
                        <label className={`flex items-start gap-2 cursor-pointer text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            <input
                                type="checkbox"
                                checked={config.params?.regularization_reminder === true}
                                onChange={(e) => updateParam('regularization_reminder', e.target.checked)}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>
                                Remind employees to regularize attendance / leave <b>7 days before</b> this runs
                                <span className={`block text-[10px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Sends email + in-app reminder. Applies to scheduled workflows only.
                                </span>
                            </span>
                        </label>
                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Locked or already-processed months are skipped automatically.
                        </p>
                    </>
                );

            case 'generate_payslips':
                return (
                    <>
                        <div>
                            <label className={labelClass}>Period</label>
                            <select
                                value={config.params?.period || 'from_trigger'}
                                onChange={(e) => updateParam('period', e.target.value)}
                                className={inputClass}
                            >
                                <option value="from_trigger">From trigger / previous step</option>
                                <option value="current_month">Current month</option>
                                <option value="previous_month">Previous month</option>
                            </select>
                        </div>
                        <label className={`flex items-start gap-2 cursor-pointer text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            <input
                                type="checkbox"
                                checked={config.params?.notify_employees !== false}
                                onChange={(e) => updateParam('notify_employees', e.target.checked)}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>Notify each employee when their payslip is ready</span>
                        </label>
                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Regenerates from the processed payroll records. Employees with a salary hold are skipped.
                        </p>
                    </>
                );

            default:
                return (
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Select an action type to configure parameters.
                    </p>
                );
        }
    };

    return (
        <div className="space-y-4">
            {/* Action Type */}
            <div>
                <label className={labelClass}>Action Type</label>
                <div className="space-y-1.5">
                    {ACTION_TYPES.map((action) => {
                        const Icon = action.icon;
                        const isSelected = config.action_type === action.value;
                        return (
                            <button
                                key={action.value}
                                onClick={() => handleTypeChange(action.value)}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                                    isSelected
                                        ? 'bg-blue-50 border border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                                        : `${isDark ? 'hover:bg-gray-700 text-gray-300 border border-transparent' : 'hover:bg-gray-100 text-gray-700 border border-transparent'}`
                                }`}
                            >
                                <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-blue-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                <span className="text-xs font-medium">{action.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Parameters */}
            {config.action_type && (
                <div className={`border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Parameters
                    </p>
                    {renderParamsForm()}
                </div>
            )}

            {/* Variable Hints */}
            <div className={`p-3 rounded-lg text-[10px] ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Available Variables</p>
                <div className={`space-y-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <p><code className="font-mono">{'{{employee.name}}'}</code> - Employee full name</p>
                    <p><code className="font-mono">{'{{employee.email}}'}</code> - Employee email</p>
                    <p><code className="font-mono">{'{{employee.department}}'}</code> - Department</p>
                    <p><code className="font-mono">{'{{employee.manager}}'}</code> - Manager name</p>
                    <p><code className="font-mono">{'{{trigger.event}}'}</code> - Trigger event</p>
                    <p><code className="font-mono">{'{{payroll.month}}'}</code> / <code className="font-mono">{'{{payroll.year}}'}</code> - Payroll period</p>
                    <p><code className="font-mono">{'{{payroll.total_employees}}'}</code> - Employees in the run</p>
                </div>
            </div>
        </div>
    );
};

export default ActionConfigForm;
